/** @format */

import React, { useState, useEffect } from "react";
import { Button, Text, ScrollArea, ActionIcon, Badge, Card } from "@mantine/core";
import { Plus, MessageCircle, Trash } from "tabler-icons-react";

// TODO: Replace localStorage with database table
// Table schema suggestion:
// - id: UUID
// - userId: Foreign key to users table
// - title: String (e.g., "Chat with 3 documents")
// - fileIds: JSON array of selected file IDs
// - conversationId: String for chat API
// - createdAt: Timestamp
// - updatedAt: Timestamp
// - lastMessage: String (optional, for preview)

const STORAGE_KEY = "document_chat_sessions";

const ChatSessionList = ({ onStartNewChat, activeChatId, onChatSelect, onDeleteChat, isProcessing = false }) => {
  const [chatSessions, setChatSessions] = useState([]);

  // Load chat sessions from localStorage on mount
  // TODO: Replace with API call to fetch user's chat sessions
  useEffect(() => {
    const loadSessions = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const sessions = JSON.parse(stored);
          setChatSessions(sessions);
        }
      } catch (error) {
        console.error("Error loading chat sessions:", error);
      }
    };
    loadSessions();

    // Refresh sessions when storage changes (from parent component)
    const handleStorageChange = () => {
      loadSessions();
    };
    
    window.addEventListener("storage", handleStorageChange);
    // Custom event for same-window updates
    window.addEventListener("chatSessionsUpdated", handleStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("chatSessionsUpdated", handleStorageChange);
    };
  }, []);

  // Refresh sessions list from localStorage
  const refreshSessions = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const sessions = JSON.parse(stored);
        setChatSessions(sessions);
      }
    } catch (error) {
      console.error("Error refreshing chat sessions:", error);
    }
  };

  // Listen for updates from parent
  useEffect(() => {
    refreshSessions();
  }, [activeChatId]);

  const handleDeleteClick = (sessionId, e) => {
    e.stopPropagation();
    onDeleteChat(sessionId);
    // Refresh the list after deletion
    setTimeout(refreshSessions, 100);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ marginBottom: "16px" }}>
        <Text size="lg" weight={600} mb={12}>
          Your Chats
        </Text>
        <Button
          fullWidth
          leftIcon={<Plus size={18} />}
          onClick={onStartNewChat}
          loading={isProcessing}
          disabled={isProcessing}
          style={{
            background: "#46BDE1",
          }}
        >
          {isProcessing ? "Processing..." : "Create New Chat"}
        </Button>
      </div>

      {/* Chat Sessions List */}
      <ScrollArea style={{ flex: 1 }}>
        {chatSessions.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
            }}
          >
            <MessageCircle size={48} color="#9CA3AF" strokeWidth={1.5} />
            <Text size="sm" color="dimmed" mt={12}>
              No chats yet
            </Text>
            <Text size="xs" color="dimmed" mt={4}>
              Click "Create New Chat" to begin
            </Text>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {chatSessions.map((session) => (
              <Card
                key={session.id}
                padding="md"
                radius="md"
                withBorder
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s",
                  backgroundColor: activeChatId === session.id ? "rgba(70, 189, 225, 0.1)" : "white",
                  borderLeft: activeChatId === session.id ? "4px solid #46BDE1" : "1px solid #E5E7EB",
                }}
                onClick={() => onChatSelect(session)}
                sx={{
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" weight={600} lineClamp={2}>
                      {session.title}
                    </Text>
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                      <Badge size="xs" variant="light" color="blue">
                        {session.fileIds.length} file{session.fileIds.length > 1 ? "s" : ""}
                      </Badge>
                      <Text size="xs" color="dimmed">
                        {new Date(session.createdAt).toLocaleDateString()}
                      </Text>
                    </div>
                    {session.fileNames && session.fileNames.length > 0 && (
                      <Text size="xs" color="dimmed" lineClamp={1} mt={4}>
                        {session.fileNames.slice(0, 2).join(", ")}
                        {session.fileNames.length > 2 && ` +${session.fileNames.length - 2}`}
                      </Text>
                    )}
                  </div>
                  <ActionIcon
                    size="sm"
                    color="red"
                    variant="subtle"
                    onClick={(e) => handleDeleteClick(session.id, e)}
                    style={{ marginLeft: "8px" }}
                  >
                    <Trash size={16} />
                  </ActionIcon>
                </div>
              </Card>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default ChatSessionList;
